/**
 * Hardware profile registry.
 *
 * Architected for scale (spec 13/84): the initial database is 301 profiles but
 * nothing here is sized for that number. Lookup is by index, search is by a
 * prebuilt inverted token index, and adding profile 10,001 costs one insert and
 * no UI change.
 *
 * Sources compose: built-in profiles, user-imported profiles from IndexedDB and
 * profiles pasted at runtime all land in the same registry and are
 * indistinguishable to the DSP.
 */

import type { AnyProfile, HardwareCategory } from '../types/hardware';
import { HARDWARE_CATEGORIES } from '../types/hardware';
import type { Confidence } from '../types/provenance';
import { summarizeConfidence } from '../types/provenance';

export interface QueryOptions {
  category?: HardwareCategory | HardwareCategory[];
  /** Free text over manufacturer, model, tags, core/topology and notes. */
  text?: string;
  brands?: string[];
  priceMin?: number;
  priceMax?: number;
  tags?: string[];
  confidence?: Confidence[];
  /** Arbitrary predicate for facets the generic filters do not cover. */
  where?: (p: AnyProfile) => boolean;
  sort?: SortKey;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  favouritesOnly?: boolean;
  favourites?: Set<string>;
}

export type SortKey = 'manufacturer' | 'model' | 'price' | 'year' | 'confidence' | 'relevance';

export interface QueryResult {
  items: AnyProfile[];
  total: number;
  /** Facet counts for the current filter set, minus the facet being counted. */
  facets: {
    brands: Array<{ value: string; count: number }>;
    tags: Array<{ value: string; count: number }>;
    confidence: Array<{ value: Confidence; count: number }>;
  };
}

const TOKEN_SPLIT = /[^a-z0-9.+]+/;

function tokenize(s: string): string[] {
  return s.toLowerCase().split(TOKEN_SPLIT).filter((t) => t.length > 0);
}

/** Everything about a profile that free text should match. */
function searchableText(p: AnyProfile): string {
  const parts: string[] = [p.manufacturer, p.model, ...(p.tags ?? []), p.category];
  if (p.notes) parts.push(p.notes);
  if (p.countryOfOrigin) parts.push(p.countryOfOrigin);
  switch (p.category) {
    case 'dac':
      parts.push(p.conversion.core, p.conversion.architecture, p.analog.stage);
      break;
    case 'amp':
      parts.push(p.topology, p.form);
      if (p.devices) parts.push(p.devices);
      break;
    case 'speaker':
      parts.push(p.speakerType, p.cabinet.enclosure, ...p.drivers);
      break;
    case 'headphone':
      parts.push(p.headphoneType, p.fit);
      break;
  }
  return parts.join(' ');
}

interface Indexed {
  profile: AnyProfile;
  tokens: Set<string>;
  confidenceScore: number;
  priceUsd: number;
}

/** Rough conversion for sorting only; the displayed price keeps its currency. */
const FX: Record<string, number> = { USD: 1, EUR: 1.08, GBP: 1.27, JPY: 0.0067 };

export class ProfileRegistry {
  private readonly byId = new Map<string, Indexed>();
  private readonly byCategory = new Map<HardwareCategory, Set<string>>();
  private readonly byBrand = new Map<string, Set<string>>();
  private readonly tokenIndex = new Map<string, Set<string>>();
  private version = 0;
  private listeners = new Set<() => void>();

  constructor() {
    for (const c of HARDWARE_CATEGORIES) {
      this.byCategory.set(c, new Set());
    }
  }

  get size(): number {
    return this.byId.size;
  }

  getVersion(): number {
    return this.version;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  register(profiles: AnyProfile[], notify = true): void {
    for (const p of profiles) this.insert(p);
    if (notify) this.notify();
  }

  private insert(p: AnyProfile): void {
    if (!p || !p.id) return;
    if (this.byId.has(p.id)) this.removeIndexes(p.id);
    const tokens = new Set(tokenize(searchableText(p)));
    const entry: Indexed = {
      profile: p,
      tokens,
      confidenceScore: summarizeConfidence(p).score,
      priceUsd: (p.price?.amount ?? 0) * (FX[p.price?.currency ?? 'USD'] ?? 1),
    };
    this.byId.set(p.id, entry);
    // Not optional-chained: a profile whose category has no index would be
    // registered but invisible to every category lookup, which is a silent
    // data-loss bug rather than a missing feature.
    let bucket = this.byCategory.get(p.category);
    if (!bucket) {
      bucket = new Set();
      this.byCategory.set(p.category, bucket);
    }
    bucket.add(p.id);
    const brandKey = p.manufacturer.toLowerCase();
    if (!this.byBrand.has(brandKey)) this.byBrand.set(brandKey, new Set());
    this.byBrand.get(brandKey)!.add(p.id);
    for (const t of tokens) {
      let set = this.tokenIndex.get(t);
      if (!set) {
        set = new Set();
        this.tokenIndex.set(t, set);
      }
      set.add(p.id);
    }
  }

  private removeIndexes(id: string): void {
    const entry = this.byId.get(id);
    if (!entry) return;
    this.byCategory.get(entry.profile.category)?.delete(id);
    this.byBrand.get(entry.profile.manufacturer.toLowerCase())?.delete(id);
    for (const t of entry.tokens) this.tokenIndex.get(t)?.delete(id);
  }

  remove(id: string): boolean {
    if (!this.byId.has(id)) return false;
    this.removeIndexes(id);
    this.byId.delete(id);
    this.notify();
    return true;
  }

  get(id: string | null | undefined): AnyProfile | null {
    if (!id) return null;
    return this.byId.get(id)?.profile ?? null;
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  all(): AnyProfile[] {
    const out: AnyProfile[] = [];
    for (const e of this.byId.values()) out.push(e.profile);
    return out;
  }

  countByCategory(c: HardwareCategory): number {
    return this.byCategory.get(c)?.size ?? 0;
  }

  brands(category?: HardwareCategory): string[] {
    const set = new Set<string>();
    for (const e of this.byId.values()) {
      if (category && e.profile.category !== category) continue;
      set.add(e.profile.manufacturer);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }

  /**
   * Duplicate-manufacturer check used by the database integrity panel.
   *
   * The one-model-per-manufacturer rule governs the CORE database. Extension
   * packs, user imports and the bundled example deliberately sit outside it, so
   * they are excluded rather than reported as violations.
   */
  duplicateManufacturers(
    category: HardwareCategory,
    exclude: (p: AnyProfile) => boolean = (p) =>
      !!p.userDefined || !!p.isExample || (p.tags ?? []).includes('extension-pack'),
  ): Array<{ manufacturer: string; ids: string[] }> {
    const map = new Map<string, string[]>();
    for (const id of this.byCategory.get(category) ?? []) {
      const p = this.byId.get(id)!.profile;
      if (exclude(p)) continue;
      const key = p.manufacturer.toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(id);
    }
    const out: Array<{ manufacturer: string; ids: string[] }> = [];
    for (const [k, ids] of map) if (ids.length > 1) out.push({ manufacturer: k, ids });
    return out;
  }

  query(opts: QueryOptions = {}): QueryResult {
    const cats = opts.category
      ? Array.isArray(opts.category)
        ? opts.category
        : [opts.category]
      : (['dac', 'amp', 'speaker', 'headphone'] as HardwareCategory[]);

    // Start from the smallest candidate set available.
    let candidates: Set<string> | null = null;
    const text = opts.text?.trim().toLowerCase() ?? '';
    const terms = text ? tokenize(text) : [];

    if (terms.length) {
      for (const term of terms) {
        const matches = new Set<string>();
        // Prefix matching so "chor" finds "chord" as the user types.
        for (const [tok, ids] of this.tokenIndex) {
          if (tok.startsWith(term)) for (const id of ids) matches.add(id);
        }
        candidates = candidates ? intersect(candidates, matches) : matches;
        if (candidates.size === 0) break;
      }
    }

    if (!candidates) {
      candidates = new Set();
      for (const c of cats) for (const id of this.byCategory.get(c) ?? []) candidates.add(id);
    }

    const brandSet = opts.brands?.length ? new Set(opts.brands.map((b) => b.toLowerCase())) : null;
    const tagSet = opts.tags?.length ? new Set(opts.tags) : null;
    const confSet = opts.confidence?.length ? new Set(opts.confidence) : null;

    const matched: Indexed[] = [];
    for (const id of candidates) {
      const e = this.byId.get(id);
      if (!e) continue;
      const p = e.profile;
      if (!cats.includes(p.category)) continue;
      if (brandSet && !brandSet.has(p.manufacturer.toLowerCase())) continue;
      if (opts.priceMin !== undefined && e.priceUsd < opts.priceMin) continue;
      if (opts.priceMax !== undefined && e.priceUsd > opts.priceMax) continue;
      if (tagSet && !(p.tags ?? []).some((t) => tagSet.has(t))) continue;
      if (confSet && !confSet.has(p.confidence)) continue;
      if (opts.favouritesOnly && !(opts.favourites?.has(p.id) ?? false)) continue;
      if (opts.where && !opts.where(p)) continue;
      matched.push(e);
    }

    // Facets computed over the matched set.
    const brandCounts = new Map<string, number>();
    const tagCounts = new Map<string, number>();
    const confCounts = new Map<Confidence, number>();
    for (const e of matched) {
      const p = e.profile;
      brandCounts.set(p.manufacturer, (brandCounts.get(p.manufacturer) ?? 0) + 1);
      for (const t of p.tags ?? []) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
      confCounts.set(p.confidence, (confCounts.get(p.confidence) ?? 0) + 1);
    }

    const dir = opts.sortDir === 'desc' ? -1 : 1;
    const sort = opts.sort ?? 'manufacturer';
    matched.sort((a, b) => {
      let r = 0;
      switch (sort) {
        case 'price': r = a.priceUsd - b.priceUsd; break;
        case 'year': r = (a.profile.year ?? 0) - (b.profile.year ?? 0); break;
        case 'confidence': r = a.confidenceScore - b.confidenceScore; break;
        case 'model': r = a.profile.model.localeCompare(b.profile.model); break;
        case 'relevance':
          r = 0;
          break;
        default:
          r = a.profile.manufacturer.localeCompare(b.profile.manufacturer);
          break;
      }
      if (r === 0) {
        r = a.profile.manufacturer.localeCompare(b.profile.manufacturer);
        if (r === 0) r = a.profile.model.localeCompare(b.profile.model);
        return r; // tie-break is always ascending, so lists stay stable
      }
      return r * dir;
    });

    const offset = opts.offset ?? 0;
    const limit = opts.limit ?? matched.length;
    return {
      items: matched.slice(offset, offset + limit).map((e) => e.profile),
      total: matched.length,
      facets: {
        brands: [...brandCounts].map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value)),
        tags: [...tagCounts].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
        confidence: [...confCounts].map(([value, count]) => ({ value, count })),
      },
    };
  }
}

function intersect(a: Set<string>, b: Set<string>): Set<string> {
  const out = new Set<string>();
  const [small, large] = a.size < b.size ? [a, b] : [b, a];
  for (const v of small) if (large.has(v)) out.add(v);
  return out;
}

export const registry = new ProfileRegistry();
